import React, { useEffect, useState } from 'react';
import './MecaplanningImportPanel.css';

const SITES_MECAPLANNING = ['SOGUAVA', 'CENTRE AUTO', 'BLUE AUTOMOBILES'];

function MecaplanningImportPanel() {
  const [concessions, setConcessions] = useState([]);
  const [dryRun, setDryRun] = useState(true);
  const [loading, setLoading] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch('/api/concessions', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setConcessions(d.concessions || []))
      .catch(() => setConcessions([]));
  }, []);

  const findConcessionForSite = (site) => {
    return concessions.find(c => (c.codes_externes || []).includes(site));
  };

  const launchImport = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/admin/mecaplanning/import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ dryRun })
      });
      const data = await res.json();
      setLastRun(data);
    } catch (e) {
      setLastRun({ success: false, error: e.message });
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (start, end) => {
    if (!start || !end) return '—';
    const ms = new Date(end) - new Date(start);
    return `${(ms / 1000).toFixed(1)}s`;
  };

  return (
    <div className="mp-import-panel">
      <div className="mp-import-header">
        <h3>🚀 Import vers Planning Atelier</h3>
        <p className="mp-import-help">
          Crée des interventions natives <code>planning_interventions</code> à partir des dossiers Mécaplanning ayant un <strong>numéro OR</strong> et un <strong>SITE</strong> reconnu. Idempotent : rejouable sans doublon.
        </p>
      </div>

      <div className="mp-import-mapping">
        <div className="mp-import-mapping-label">Concessions mappées via <code>codes_externes</code> :</div>
        <ul className="mp-import-sites">
          {SITES_MECAPLANNING.map(site => {
            const c = findConcessionForSite(site);
            return (
              <li key={site} className={c ? 'mp-site-ok' : 'mp-site-ko'}>
                <span className="mp-site-name">{site}</span>
                <span className="mp-site-arrow">→</span>
                <span className="mp-site-target">{c ? c.nom : 'Non mappé'}</span>
                <span className="mp-site-icon">{c ? '✅' : '❌'}</span>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="mp-import-controls">
        <label className="mp-import-checkbox">
          <input type="checkbox" checked={dryRun} onChange={e => setDryRun(e.target.checked)} disabled={loading} />
          <span>Simulation (dry-run, aucune écriture)</span>
        </label>
        <button
          className="page-action-btn"
          onClick={launchImport}
          disabled={loading}
        >
          {loading ? '⏳ En cours…' : (dryRun ? '▶ Simuler' : '🚀 Lancer / Re-synchroniser')}
        </button>
      </div>

      {lastRun && (
        <div className={`mp-import-result ${lastRun.success ? 'mp-success' : 'mp-error'}`}>
          {lastRun.success ? (
            <>
              <div className="mp-import-summary">
                <strong>{lastRun.dryRun ? 'Simulation terminée' : 'Import terminé'}</strong>
                {' '}— {lastRun.eligible || 0} éligibles ·
                {' '}<span className="mp-stat-good">{lastRun.imported || 0} créées</span> ·
                {' '}<span className="mp-stat-info">{lastRun.updated || 0} mises à jour</span> ·
                {' '}<span className="mp-stat-warn">{lastRun.rejected || 0} rejets</span>
                {' '}({formatDuration(lastRun.startedAt, lastRun.finishedAt)})
              </div>
              <div className="mp-import-substats">
                Mécanicien matché : {lastRun.matchedStaff || 0} · Fallback texte : {lastRun.fallbackStaff || 0}
                {((lastRun.rejects && lastRun.rejects.length) || (lastRun.warnings && lastRun.warnings.length)) ? (
                  <button className="mp-import-toggle-details" onClick={() => setShowDetails(s => !s)}>
                    {showDetails ? 'Masquer' : 'Voir'} les détails
                  </button>
                ) : null}
              </div>
              {showDetails && (
                <div className="mp-import-details">
                  {lastRun.warnings?.length > 0 && (
                    <details open>
                      <summary>⚠ Warnings ({lastRun.warnings.length})</summary>
                      <ul>
                        {lastRun.warnings.slice(0, 50).map((w, i) => (
                          <li key={i}>#{w.mp_id} — {w.message}</li>
                        ))}
                        {lastRun.warnings.length > 50 && <li>… et {lastRun.warnings.length - 50} de plus</li>}
                      </ul>
                    </details>
                  )}
                  {lastRun.rejects?.length > 0 && (
                    <details open>
                      <summary>❌ Rejets ({lastRun.rejects.length})</summary>
                      <ul>
                        {lastRun.rejects.slice(0, 50).map((r, i) => (
                          <li key={i}>#{r.mp_id} ({r.immat || '—'}) — {r.reason}</li>
                        ))}
                        {lastRun.rejects.length > 50 && <li>… et {lastRun.rejects.length - 50} de plus</li>}
                      </ul>
                    </details>
                  )}
                </div>
              )}
            </>
          ) : (
            <div>❌ {lastRun.error || 'Erreur inconnue'}</div>
          )}
        </div>
      )}
    </div>
  );
}

export default MecaplanningImportPanel;
